import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    X, Copy, UserCheck, Loader2, Briefcase, BarChart3, Check, Globe
} from 'lucide-react';
import { useJobDescriptions } from '../../../../hooks/useCandidateDetailApis';
import api from '../../../../api/client';
import { useNavigate } from 'react-router-dom';

const DEFAULT_INTERVIEW_DURATION_MINUTES = 30;
const MIN_MAX_NO_OF_QUESTIONS = 5;
const MIN_INTERVIEW_DURATION = 15;
const MAX_INTERVIEW_DURATION = 60;

const parseRequireAudioVerification = (value, defaultValue = true) => {
    if (value === false || value === 'false' || value === 0 || value === '0') return false;
    if (value === true || value === 'true' || value === 1 || value === '1') return true;
    return defaultValue;
};

const MultiJDScheduleModal = ({
    isOpen,
    onClose,
    selectedCandidate,
    onCreateInvites,
    onCreateInvitesWithSkills,
    onSkillAnalysis,
    selectedJDs,
    setSelectedJDs,
    selectedSkills,
    setSelectedSkills,
    multiJDResults,
    isCreatingInvites,
    setSnackbar
}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [searchQuery, setSearchQuery] = useState('');
    const [localSearchQuery, setLocalSearchQuery] = useState('');
    const [selectedLanguages, setSelectedLanguages] = useState({}); // Language per JD
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [loadingLanguages, setLoadingLanguages] = useState(false);
    const [maxNoOfQuestions, setMaxNoOfQuestions] = useState('');
    const [interviewDurationMinutes, setInterviewDurationMinutes] = useState(String(DEFAULT_INTERVIEW_DURATION_MINUTES));
    const [requireAudioVerification, setRequireAudioVerification] = useState(true);
    const [showGlobalConfigEditor, setShowGlobalConfigEditor] = useState(false);
    const [jdConfigOverrides, setJdConfigOverrides] = useState({});
    const [scheduleErrors, setScheduleErrors] = useState({});
    const [, setCurrentLanguage] = useState('en'); // Current UI language

    const {
        jobDescriptions,
        paginationData,
        fetchJobDescriptions,
        loading
    } = useJobDescriptions();

    // Get current UI language and update when it changes
    useEffect(() => {
        const uiLang = localStorage.getItem('i18nextLng') || 'en';
        setCurrentLanguage(uiLang);
        
        // Update all selected languages when UI language changes
        if (selectedJDs.length > 0) {
            const updatedLanguages = {};
            selectedJDs.forEach(jdId => {
                updatedLanguages[jdId] = uiLang;
            });
            setSelectedLanguages(updatedLanguages);
        }
    },// eslint-disable-next-line react-hooks/exhaustive-deps
     [localStorage.getItem('i18nextLng')]);

    // Fetch company's available languages
    useEffect(() => {
        const fetchCompanyLanguages = async () => {
            if (!isOpen || !selectedCandidate) return;
            
            setLoadingLanguages(true);
            try {
                let companyId = selectedCandidate.company?.[0]?.id;
                
                if (!companyId) {
                    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
                    if (token) {
                        try {
                            const payload = JSON.parse(atob(token.split('.')[1]));
                            companyId = payload.company_id;
                        } catch (e) {
                            console.warn('Could not decode token for company_id');
                        }
                    }
                }

                if (companyId) {
                    const companyResponse = await api.get(`/companies/${companyId}/`);
                    const company = companyResponse.data;
                    const interviewSettings = company?.settings?.interview_settings || {};
                    const companyMaxQuestions = interviewSettings.max_no_of_questions;
                    const companyInterviewDuration = interviewSettings.interview_duration_minutes;
                    setMaxNoOfQuestions(
                        companyMaxQuestions == null || companyMaxQuestions === ''
                            ? ''
                            : String(companyMaxQuestions)
                    );
                    setInterviewDurationMinutes(
                        companyInterviewDuration == null || companyInterviewDuration === ''
                            ? String(DEFAULT_INTERVIEW_DURATION_MINUTES)
                            : String(companyInterviewDuration)
                    );
                    setRequireAudioVerification(parseRequireAudioVerification(interviewSettings.require_audio_verification, true));
                    if (company.available_languages_detail && company.available_languages_detail.length > 0) {
                        setAvailableLanguages(company.available_languages_detail);
                    } else {
                        const languagesResponse = await api.get('/languages/');
                        const allLanguages = languagesResponse.data.results || languagesResponse.data || [];
                        setAvailableLanguages(allLanguages);
                    }
                } else {
                    const languagesResponse = await api.get('/languages/');
                    const allLanguages = languagesResponse.data.results || languagesResponse.data || [];
                    setAvailableLanguages(allLanguages);
                    setMaxNoOfQuestions('');
                    setInterviewDurationMinutes(String(DEFAULT_INTERVIEW_DURATION_MINUTES));
                    setRequireAudioVerification(true);
                }
            } catch (error) {
                console.error('Error fetching languages:', error);
                try {
                    const languagesResponse = await api.get('/languages/');
                    const allLanguages = languagesResponse.data.results || languagesResponse.data || [];
                    setAvailableLanguages(allLanguages);
                    setMaxNoOfQuestions('');
                    setInterviewDurationMinutes(String(DEFAULT_INTERVIEW_DURATION_MINUTES));
                    setRequireAudioVerification(true);
                } catch (e) {
                    console.error('Error fetching all languages:', e);
                }
            } finally {
                setLoadingLanguages(false);
            }
        };

        fetchCompanyLanguages();
    }, [isOpen, selectedCandidate]);

    // debounce search
    useEffect(() => {
        const timer = setTimeout(() => setSearchQuery(localSearchQuery), 300);
        return () => clearTimeout(timer);
    }, [localSearchQuery]);

    useEffect(() => {
        fetchJobDescriptions(1, 10, searchQuery);
    }, [searchQuery,fetchJobDescriptions]);

    if (!isOpen) return null;

    const handleJDSelection = (jdId) => {
        setSelectedJDs((prev) => {
            const isCurrentlySelected = prev.includes(jdId);
            if (isCurrentlySelected) {
                // Remove JD and its language
                setSelectedLanguages(prevLangs => {
                    const newLangs = { ...prevLangs };
                    delete newLangs[jdId];
                    return newLangs;
                });
                setJdConfigOverrides(prev => {
                    const next = { ...prev };
                    delete next[jdId];
                    return next;
                });
                return prev.filter((id) => id !== jdId);
            } else {
                // Add JD and set language to current UI language
                const currentUiLang = localStorage.getItem('i18nextLng') || 'en';
                setSelectedLanguages(prevLangs => ({
                    ...prevLangs,
                    [jdId]: currentUiLang
                }));
                return [...prev, jdId];
            }
        });
    };

    const handleClose = () => {
        onClose();
        setSelectedJDs([]);
        setSelectedSkills({});
        setSearchQuery('');
        setLocalSearchQuery('');
        setSelectedLanguages({});
        setJdConfigOverrides({});
        setScheduleErrors({});
        setShowGlobalConfigEditor(false);
    };

    const validateConfig = (maxQuestionsValue, durationValue, prefix = '') => {
        const errors = {};
        const trimmedMaxQuestions = String(maxQuestionsValue ?? '').trim();
        const trimmedDuration = String(durationValue ?? '').trim();

        if (trimmedMaxQuestions !== '') {
            const parsedMaxQuestions = Number(trimmedMaxQuestions);
            if (!Number.isFinite(parsedMaxQuestions) || !Number.isInteger(parsedMaxQuestions)) {
                errors[`${prefix}max_no_of_questions`] = t('candidateDetails.inviteSchedule.validation.maxQuestionsInteger');
            } else if (parsedMaxQuestions < MIN_MAX_NO_OF_QUESTIONS) {
                errors[`${prefix}max_no_of_questions`] = t('candidateDetails.inviteSchedule.validation.maxQuestionsMin', { min: MIN_MAX_NO_OF_QUESTIONS });
            }
        }

        const parsedDuration = Number(trimmedDuration);
        if (!Number.isFinite(parsedDuration) || !Number.isInteger(parsedDuration)) {
            errors[`${prefix}interview_duration_minutes`] = t('candidateDetails.inviteSchedule.validation.durationInteger');
        } else if (parsedDuration < MIN_INTERVIEW_DURATION || parsedDuration > MAX_INTERVIEW_DURATION) {
            errors[`${prefix}interview_duration_minutes`] = t('candidateDetails.inviteSchedule.validation.durationRange', {
                min: MIN_INTERVIEW_DURATION,
                max: MAX_INTERVIEW_DURATION
            });
        }
        return errors;
    };

    const getEffectiveConfig = (jdId) => {
        const override = jdConfigOverrides[jdId];
        if (override?.enabled) {
            return {
                max_no_of_questions: override.max_no_of_questions === '' ? null : Number(override.max_no_of_questions),
                interview_duration_minutes: Number(override.interview_duration_minutes),
                is_override: true
            };
        }
        return {
            max_no_of_questions: maxNoOfQuestions.trim() === '' ? null : Number(maxNoOfQuestions),
            interview_duration_minutes: Number(interviewDurationMinutes),
            is_override: false
        };
    };

    const getScheduleConfig = () => {
        const errors = {
            ...validateConfig(maxNoOfQuestions, interviewDurationMinutes, 'global_')
        };
        const overrides = {};

        selectedJDs.forEach((jdId) => {
            const override = jdConfigOverrides[jdId];
            if (!override?.enabled) return;
            Object.assign(errors, validateConfig(
                override.max_no_of_questions,
                override.interview_duration_minutes,
                `jd_${jdId}_`
            ));
            overrides[jdId] = {
                max_no_of_questions: override.max_no_of_questions === '' ? null : Number(override.max_no_of_questions),
                interview_duration_minutes: Number(override.interview_duration_minutes)
            };
        });

        setScheduleErrors(errors);
        if (Object.keys(errors).length > 0) {
            if (errors.global_max_no_of_questions || errors.global_interview_duration_minutes) {
                setShowGlobalConfigEditor(true);
            }
            return null;
        }

        return {
            global: {
                max_no_of_questions: maxNoOfQuestions.trim() === '' ? null : Number(maxNoOfQuestions),
                interview_duration_minutes: Number(interviewDurationMinutes),
                require_audio_verification: requireAudioVerification
            },
            overrides
        };
    };

    const handleCreateInvites = () => {
        // Check if all selected JDs have a language
        const missingLanguages = selectedJDs.filter(jdId => !selectedLanguages[jdId]);
        if (missingLanguages.length > 0) {
            setSnackbar({
                open: true,
                message: t('candidateDetails.multiJDSchedule.selectLanguageError'),
                severity: 'error'
            });
            return;
        }
        const scheduleConfig = getScheduleConfig();
        if (!scheduleConfig) return;
        if (Object.keys(selectedSkills).length > 0) {
            onCreateInvitesWithSkills(selectedLanguages, scheduleConfig);
        } else {
            onCreateInvites(selectedLanguages, scheduleConfig);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-xl w-full max-w-5xl max-h-[95vh] flex flex-col shadow-xl">

                {/* HEADER */}
                <div
    className="flex justify-between items-start p-3 border-b"
    style={{ background: 'linear-gradient(135deg, #0ea5e9, #d946ef)' }}
>
    <div>
        <h2 className="text-white text-xl font-semibold mb-0.5">
            {t('candidateDetails.multiJDSchedule.title')}
        </h2>

        <p className="text-blue-100 text-lg leading-snug">
            {t('candidateDetails.multiJDSchedule.selectFor', { name: `${selectedCandidate?.firstName} ${selectedCandidate?.lastName}` })}
        </p>
    </div>

    <button
        onClick={handleClose}
        className="text-white hover:bg-white/20 rounded-lg p-1.5"
    >
        <X size={18} />
    </button>
</div>


                {/* CONTENT */}
                <div className="flex-1 overflow-y-auto p-4">

                    {multiJDResults ? (
                        <div className="space-y-4">

                            {/* SUCCESS SUMMARY */}
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex gap-3 items-center">
                                    <div className="bg-green-100 rounded-full p-2.5">
                                        <Check className="text-green-600" size={20} />
                                    </div>
                                    <div>
                                        <h3 className="text-green-800 font-semibold text-base">
                                            {t('candidateDetails.multiJDSchedule.createdSuccessfully')}
                                        </h3>
                                        <p className="text-green-700 text-sm mt-1">
                                            {t('candidateDetails.multiJDSchedule.outOf', { count: multiJDResults.totalSuccessful, total: multiJDResults.totalSuccessful + multiJDResults.totalFailed })}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* SUCCESS LIST */}
                            {multiJDResults.successfulInvites?.length > 0 && (
                                <div className="bg-white border rounded-lg p-4">
                                    <h4 className="font-semibold text-gray-900 text-base text-lg mb-3">
                                        {t('candidateDetails.multiJDSchedule.successfulInvites')}
                                    </h4>
                                    <div className="space-y-3">
                                        {multiJDResults.successfulInvites.map((invite, i) => (
                                            <div key={i} className="bg-gray-50 border rounded-lg p-3 flex justify-between items-center">
                                                <span className="font-medium text-gray-900 text-md">{invite.jobDescriptionTitle}</span>
                                                <div className="flex gap-2 items-center">
                                                    <input
                                                        type="text"
                                                        value={invite.interviewUrl}
                                                        readOnly
                                                        className="border rounded px-2 py-1 text-md bg-white w-48"
                                                    />
                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(invite.interviewUrl);
                                                            setSnackbar({ open: true, message: t('candidateDetails.multiJDSchedule.urlCopied'), severity: 'success' });
                                                        }}
                                                        className="p-2 hover:bg-gray-200 rounded"
                                                    >
                                                        <Copy size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* FAILED LIST */}
                            {multiJDResults.failedInvites?.length > 0 && (
                                <div className="bg-white border border-red-200 rounded-lg p-4">
                                    <h4 className="font-semibold text-red-900 text-base mb-3">
                                        {t('candidateDetails.multiJDSchedule.failedInvites')}
                                    </h4>
                                    <div className="space-y-3">
                                        {multiJDResults.failedInvites.map((fail, i) => (
                                            <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3">
                                                <p className="font-medium text-red-900 text-sm">{fail.jobDescriptionTitle}</p>
                                                <p className="text-red-700 text-xs mt-1">{fail.error}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </div>
                    ) : (
                        <div className="space-y-5">

                            {/* INFO + GLOBAL CONFIG (COMBINED) */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="flex gap-3 items-start min-w-[260px]">
                                        <div className="bg-blue-100 rounded-full p-2.5">
                                            <UserCheck size={18} className="text-blue-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-blue-900 text-base">
                                                {t('candidateDetails.multiJDSchedule.infoTitle')}
                                            </h3>
                                            <p className="text-blue-800 text-sm mt-0.5">
                                                {t('candidateDetails.multiJDSchedule.infoDescription')}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-[260px]">
                                        <div className="flex flex-wrap items-center gap-2 justify-start md:justify-end">
                                            <button
                                                type="button"
                                                onClick={() => navigate('/companyadmins?tab=interviewConfiguration')}
                                                className="text-xs font-semibold text-gray-800 hover:text-blue-700 hover:underline"
                                            >
                                                {t('candidateDetails.inviteSchedule.globalConfigLink')}
                                            </button>
                                            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                                                {t('candidateDetails.inviteSchedule.tagQuestions')}{' '}
                                                <span className="font-semibold">
                                                    {maxNoOfQuestions.trim() === '' ? t('candidateDetails.inviteSchedule.unlimited') : maxNoOfQuestions}
                                                </span>
                                            </span>
                                            <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">
                                                {t('candidateDetails.inviteSchedule.tagDuration')}{' '}
                                                <span className="font-semibold">{interviewDurationMinutes}</span>
                                                {t('candidateDetails.inviteSchedule.minutesSuffix')}
                                            </span>
                                            <span className={`text-xs px-2 py-1 rounded-full ${requireAudioVerification ? 'bg-sky-100 text-sky-800' : 'bg-gray-100 text-gray-700'}`}>
                                                {t('candidateDetails.inviteSchedule.tagAudio', 'Audio:')}{' '}
                                                <span className="font-semibold">
                                                    {requireAudioVerification
                                                        ? t('candidateDetails.inviteSchedule.audioVerificationRequired', 'Required')
                                                        : t('candidateDetails.inviteSchedule.audioVerificationOptional', 'Optional')}
                                                </span>
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setShowGlobalConfigEditor(prev => !prev)}
                                                className="text-xs text-blue-700 hover:underline"
                                            >
                                                {showGlobalConfigEditor ? t('candidateDetails.inviteSchedule.hide') : t('candidateDetails.inviteSchedule.change')}
                                            </button>
                                        </div>
                                        {showGlobalConfigEditor && (
                                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-xs text-gray-700 mb-1">{t('candidateDetails.inviteSchedule.maxQuestionsLabelShort')}</label>
                                                    <input
                                                        type="number"
                                                        min={MIN_MAX_NO_OF_QUESTIONS}
                                                        step={1}
                                                        placeholder={t('candidateDetails.inviteSchedule.unlimitedPlaceholder')}
                                                        value={maxNoOfQuestions}
                                                        onChange={(e) => {
                                                            setMaxNoOfQuestions(e.target.value);
                                                            if (scheduleErrors.global_max_no_of_questions) {
                                                                setScheduleErrors(prev => ({ ...prev, global_max_no_of_questions: null }));
                                                            }
                                                        }}
                                                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs bg-white"
                                                    />
                                                    {scheduleErrors.global_max_no_of_questions && (
                                                        <p className="text-xs text-red-600 mt-1">{scheduleErrors.global_max_no_of_questions}</p>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-700 mb-1">{t('candidateDetails.inviteSchedule.durationLabelShort')}</label>
                                                    <input
                                                        type="number"
                                                        min={MIN_INTERVIEW_DURATION}
                                                        max={MAX_INTERVIEW_DURATION}
                                                        step={1}
                                                        value={interviewDurationMinutes}
                                                        onChange={(e) => {
                                                            setInterviewDurationMinutes(e.target.value);
                                                            if (scheduleErrors.global_interview_duration_minutes) {
                                                                setScheduleErrors(prev => ({ ...prev, global_interview_duration_minutes: null }));
                                                            }
                                                        }}
                                                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs bg-white"
                                                    />
                                                    {scheduleErrors.global_interview_duration_minutes && (
                                                        <p className="text-xs text-red-600 mt-1">{scheduleErrors.global_interview_duration_minutes}</p>
                                                    )}
                                                </div>
                                                <div className="md:col-span-2 flex items-start justify-between gap-3 pt-1">
                                                    <div className="min-w-0">
                                                        <label className="block text-xs text-gray-700">{t('candidateDetails.inviteSchedule.audioVerificationLabel', 'Audio verification')}</label>
                                                        <p className="text-[11px] text-gray-500 mt-0.5">{t('candidateDetails.inviteSchedule.audioVerificationHelp', 'Company default is shown. Turn off to skip the spoken microphone check for this invite.')}</p>
                                                    </div>
                                                    <div className="flex shrink-0 items-center gap-2">
                                                        <span className="text-xs text-gray-600">
                                                            {requireAudioVerification
                                                                ? t('candidateDetails.inviteSchedule.audioVerificationRequired', 'Required')
                                                                : t('candidateDetails.inviteSchedule.audioVerificationOptional', 'Optional')}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            role="switch"
                                                            aria-checked={requireAudioVerification}
                                                            onClick={() => setRequireAudioVerification(prev => !prev)}
                                                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                                                requireAudioVerification ? 'bg-blue-600' : 'bg-gray-200'
                                                            }`}
                                                        >
                                                            <span
                                                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                                    requireAudioVerification ? 'translate-x-4' : 'translate-x-0.5'
                                                                }`}
                                                            />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {(scheduleErrors.global_max_no_of_questions || scheduleErrors.global_interview_duration_minutes) && !showGlobalConfigEditor && (
                                            <p className="text-xs text-red-600 mt-2 md:text-right">
                                                {t('candidateDetails.inviteSchedule.fixGlobalConfigHint')}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>



                            {/* TOP BAR */}
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <Briefcase size={18} className="text-gray-700" />
                                    <h3 className="font-semibold text-gray-900 text-md text-base">
                                        {t('candidateDetails.multiJDSchedule.availableJDs')}
                                    </h3>
                                    <p className='text-md text-bold'>
                                        {t('candidateDetails.multiJDSchedule.selectedJDs')}
                                    </p>
                                    <span className="bg-blue-600 text-white text-sm font-semibold px-3 py-1 rounded-full">
                                        {selectedJDs.length}
                                    </span>
                                </div>

                                <input
                                    type="text"
                                    placeholder={t('candidateDetails.multiJDSchedule.searchPlaceholder')}
                                    value={localSearchQuery}
                                    onChange={(e) => setLocalSearchQuery(e.target.value)}
                                    className="w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-md"
                                />
                            </div>

                            {/* JOB LIST */}
                            <div className="min-h-[360px]">
                                {loading ? (
                                    <div className="flex items-center justify-center h-[360px]">
                                        <Loader2 size={32} className="animate-spin text-blue-600" />
                                    </div>
                                ) : jobDescriptions.length === 0 ? (
                                    <div className="flex flex-col justify-center items-center h-[360px] bg-gray-50 border rounded-lg">
                                        <Briefcase size={42} className="text-gray-300 mb-3" />
                                        <p className="text-gray-500 text-md font-medium">
                                            {t('candidateDetails.multiJDSchedule.noJobDescriptions')}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                                        {jobDescriptions.map((jd) => {
                                            const isSelected = selectedJDs.includes(jd.id);
                                            const effectiveConfig = getEffectiveConfig(jd.id);
                                            const currentOverride = jdConfigOverrides[jd.id];

                                            return (
                                                <div
                                                    key={jd.id}
                                                    onClick={() => handleJDSelection(jd.id)}
                                                    className={`rounded-lg cursor-pointer border transition-all
                                                        ${isSelected
                                                            ? "border-blue-500 bg-blue-50 shadow-sm"
                                                            : "border-gray-200 bg-white hover:shadow-sm"}
                                                    `}
                                                >
                                                    <div className="flex gap-3 p-4">

                                                        {/* Checkbox */}
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handleJDSelection(jd.id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="mt-1 h-4 w-4 text-blue-600 border-gray-300"
                                                        />

                                                        {/* Content */}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1.5">
                                                                <Briefcase size={14} className="text-gray-400" />
                                                                <h4 className="text-md font-semibold text-gray-900 truncate">{jd.title}</h4>
                                                            </div>

                                                            <div className="flex items-center gap-2 text-xs mb-2">
                                                                {jd.department_name && (
                                                                    <>
                                                                        <span className="text-blue-600 text-sm font-medium">
                                                                            {jd.department_name}
                                                                        </span>
                                                                        <span className="text-gray-300">•</span>
                                                                    </>
                                                                )}

                                                                <span className="text-gray-500 text-sm">
                                                                    {new Date(jd.created_at).toLocaleDateString(i18n.language, {
                                                                        day: "numeric",
                                                                        month: "short",
                                                                        year: "numeric",
                                                                    })}
                                                                </span>
                                                            </div>

                                                            {jd.description && (
                                                                <p className="text-md text-gray-600 line-clamp-2">
                                                                    {jd.description.replace(/<[^>]*>/g, "").substring(0, 120)}...
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Language Selection for this JD */}
                                                    {isSelected && availableLanguages.length > 0 && (
                                                        <div className="px-4 pb-3">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <Globe size={14} className="text-gray-600" />
                                                                <label className="text-sm font-medium text-gray-700">
                                                                    {t('candidateDetails.multiJDSchedule.interviewLanguage')}
                                                                </label>
                                                                <span className="text-red-500 text-sm">*</span>
                                                            </div>
                                                            <select
                                                                value={selectedLanguages[jd.id] || ''}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedLanguages(prev => ({
                                                                        ...prev,
                                                                        [jd.id]: e.target.value
                                                                    }));
                                                                }}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                                disabled={loadingLanguages}
                                                            >
                                                                {loadingLanguages ? (
                                                                    <option value="">{t('common.loading')}...</option>
                                                                ) : (
                                                                    availableLanguages.map(lang => (
                                                                        <option key={lang.code} value={lang.code}>
                                                                            {lang.name} ({lang.native_name})
                                                                        </option>
                                                                    ))
                                                                )}
                                                            </select>
                                                        </div>
                                                    )}

                                                    {isSelected && (
                                                        <div className="px-4 pb-3">
                                                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                                                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                                                    {t('candidateDetails.inviteSchedule.tagQuestions')}{' '}
                                                                    {effectiveConfig.max_no_of_questions == null ? t('candidateDetails.inviteSchedule.unlimited') : effectiveConfig.max_no_of_questions}
                                                                </span>
                                                                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                                                    {t('candidateDetails.inviteSchedule.tagDuration')}{' '}
                                                                    {effectiveConfig.interview_duration_minutes}{t('candidateDetails.inviteSchedule.minutesSuffix')}
                                                                </span>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        setJdConfigOverrides(prev => {
                                                                            const existing = prev[jd.id];
                                                                            const enabled = !(existing?.enabled);
                                                                            if (!enabled) return { ...prev, [jd.id]: { ...existing, enabled: false } };
                                                                            return {
                                                                                ...prev,
                                                                                [jd.id]: {
                                                                                    enabled: true,
                                                                                    max_no_of_questions: existing?.max_no_of_questions ?? maxNoOfQuestions,
                                                                                    interview_duration_minutes: existing?.interview_duration_minutes ?? interviewDurationMinutes
                                                                                }
                                                                            };
                                                                        });
                                                                    }}
                                                                    className="text-xs px-2 py-1 rounded-full border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100"
                                                                >
                                                                    {currentOverride?.enabled ? t('candidateDetails.inviteSchedule.jdOverrideActive') : t('candidateDetails.inviteSchedule.jdOverridePrompt')}
                                                                </button>
                                                            </div>
                                                            {currentOverride?.enabled && (
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                                    <div>
                                                                        <input
                                                                            type="number"
                                                                            min={MIN_MAX_NO_OF_QUESTIONS}
                                                                            step={1}
                                                                            placeholder={t('candidateDetails.inviteSchedule.unlimitedPlaceholder')}
                                                                            value={currentOverride.max_no_of_questions ?? ''}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value;
                                                                                setJdConfigOverrides(prev => ({
                                                                                    ...prev,
                                                                                    [jd.id]: {
                                                                                        ...prev[jd.id],
                                                                                        enabled: true,
                                                                                        max_no_of_questions: value
                                                                                    }
                                                                                }));
                                                                                const k = `jd_${jd.id}_max_no_of_questions`;
                                                                                if (scheduleErrors[k]) setScheduleErrors(prev => ({ ...prev, [k]: null }));
                                                                            }}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                                                                        />
                                                                        {scheduleErrors[`jd_${jd.id}_max_no_of_questions`] && (
                                                                            <p className="text-xs text-red-600 mt-1">{scheduleErrors[`jd_${jd.id}_max_no_of_questions`]}</p>
                                                                        )}
                                                                    </div>
                                                                    <div>
                                                                        <input
                                                                            type="number"
                                                                            min={MIN_INTERVIEW_DURATION}
                                                                            max={MAX_INTERVIEW_DURATION}
                                                                            step={1}
                                                                            value={currentOverride.interview_duration_minutes ?? ''}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value;
                                                                                setJdConfigOverrides(prev => ({
                                                                                    ...prev,
                                                                                    [jd.id]: {
                                                                                        ...prev[jd.id],
                                                                                        enabled: true,
                                                                                        interview_duration_minutes: value
                                                                                    }
                                                                                }));
                                                                                const k = `jd_${jd.id}_interview_duration_minutes`;
                                                                                if (scheduleErrors[k]) setScheduleErrors(prev => ({ ...prev, [k]: null }));
                                                                            }}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                                                                        />
                                                                        {scheduleErrors[`jd_${jd.id}_interview_duration_minutes`] && (
                                                                            <p className="text-xs text-red-600 mt-1">{scheduleErrors[`jd_${jd.id}_interview_duration_minutes`]}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {isSelected && (
                                                        <div className="px-4 pb-4">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    onSkillAnalysis(jd);
                                                                }}
                                                                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm hover:opacity-90"
                                                                style={{ background: 'linear-gradient(135deg, #0ea5e9, #d946ef)' }}
                                                            >
                                                                <BarChart3 size={16} />
                                                                {t('candidateDetails.multiJDSchedule.analyzeSkills')}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* SELECTED SKILLS SUMMARY */}
                            {Object.keys(selectedSkills).length > 0 && (
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="p-2 rounded-full" style={{ background: 'linear-gradient(135deg, #0ea5e9, #d946ef)' }}>
                                            <BarChart3 size={14} className="text-white" />
                                        </div>
                                        <h3 className="font-semibold text-sm text-purple-900">
                                            {t('candidateDetails.multiJDSchedule.selectedSkills')}
                                        </h3>
                                    </div>

                                    <div className="space-y-2">
                                        {Object.entries(selectedSkills).map(([jdId, skills]) => {
                                            const jd = jobDescriptions.find((j) => j.id === parseInt(jdId));
                                            if (!jd || skills.length === 0) return null;

                                            return (
                                                <div key={jdId} className="bg-white border border-purple-200 rounded-lg p-3">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Briefcase size={12} className="text-purple-600" />
                                                        <h4 className="font-semibold text-xs text-purple-800">{jd.title}</h4>
                                                        <span className="text-xs text-purple-600">({skills.length})</span>
                                                    </div>

                                                    <div className="flex flex-wrap gap-1.5">
                                                        {skills.map((skill, i) => (
                                                            <span
                                                                key={i}
                                                                className="px-2 py-1 text-xs font-medium text-white rounded"
                                                                style={{ background: 'linear-gradient(135deg, #0ea5e9, #d946ef)' }}
                                                            >
                                                                {skill}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <button
                                        onClick={() => setSelectedSkills({})}
                                        className="text-xs text-purple-700 mt-3 hover:underline"
                                    >
                                        {t('candidateDetails.multiJDSchedule.clearAllSkills')}
                                    </button>
                                </div>
                            )}

                        </div>
                    )}

                </div>

                {/* FOOTER */}
                <div className="border-t p-2.5 bg-gray-50 rounded-b-xl">
                    {multiJDResults ? (
                        <div className="flex justify-end">
                            <button
                                onClick={handleClose}
                                className="px-5 py-2 bg-gray-600 text-white rounded-lg text-md hover:bg-gray-700"
                            >
                                {t('candidateDetails.multiJDSchedule.close')}
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between">

                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleClose}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-md hover:bg-white"
                                >
                                    {t('candidateDetails.multiJDSchedule.cancel')}
                                </button>

                                {/* PAGINATION */}
                                <div className="flex items-center gap-2 ml-5">
                                    <button
                                        onClick={() => fetchJobDescriptions(paginationData.current_page - 1, 10, searchQuery)}
                                        disabled={!paginationData.has_previous}
                                        className="px-4 py-2 ml-5 border border-gray-300 text-gray-700 bg-white text-md rounded-lg disabled:opacity-40"
                                    >
                                        {t('candidateDetails.multiJDSchedule.previous')}
                                    </button>

                                    <span className="text-md text-gray-700">
                                        {paginationData.total_pages > 0
                                            ? t('candidateDetails.multiJDSchedule.pageInfo', { current: paginationData.current_page, total: paginationData.total_pages })
                                            : t('candidateDetails.multiJDSchedule.pageInfo', { current: 0, total: 0 })}
                                    </span>

                                    <button
                                        onClick={() => fetchJobDescriptions(paginationData.current_page + 1, 10, searchQuery)}
                                        disabled={!paginationData.has_next}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 bg-white text-md rounded-lg disabled:opacity-40"
                                    >
                                        {t('candidateDetails.multiJDSchedule.next')}
                                    </button>
                                </div>
                            </div>

                            <button
                                onClick={handleCreateInvites}
                                disabled={selectedJDs.length === 0 || selectedJDs.some(jdId => !selectedLanguages[jdId]) || isCreatingInvites}
                                className="px-5 py-2 text-white rounded-lg text-md flex items-center gap-2 disabled:opacity-50 hover:opacity-90"
                                style={{ background: 'linear-gradient(135deg, #0ea5e9, #d946ef)' }}
                            >
                                {isCreatingInvites ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        {t('candidateDetails.multiJDSchedule.creating')}
                                    </>
                                ) : (
                                    <>
                                        <UserCheck size={16} />
                                        {selectedJDs.length === 1 
                                            ? t('candidateDetails.multiJDSchedule.createInvite', { count: selectedJDs.length })
                                            : t('candidateDetails.multiJDSchedule.createInvites', { count: selectedJDs.length })
                                        }
                                    </>
                                )}
                            </button>

                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default MultiJDScheduleModal;
